import type { AttachmentDto, PageAttachmentDto } from '@/api/attachment';
import { narrowEnum, nullableString } from '@/api/mappers/_shared';
import type { DocumentType, DocumentViewModel } from '@/features/patient-record/types';

const KNOWN_DOCUMENT_TYPE: ReadonlySet<DocumentType> = new Set<DocumentType>([
  'DOCUMENT',
  'IMAGE',
  'MEDICAL_REPORT',
  'QUESTIONNAIRE',
]);

function formatDateObserved(
  value: AttachmentDto['dateObserved'],
): string | null {
  if (value === null || value === undefined) return null;
  const year = typeof value.year === 'number' ? value.year : null;
  if (year === null || year <= 0) return null;
  const parts = [String(year).padStart(4, '0')];
  const month = typeof value.month === 'number' && value.month > 0 ? value.month : null;
  const day = typeof value.day === 'number' && value.day > 0 ? value.day : null;
  if (month !== null) {
    parts.push(String(month).padStart(2, '0'));
    if (day !== null) parts.push(String(day).padStart(2, '0'));
  }
  return parts.join('-');
}

export function mapDocument(dto: AttachmentDto): DocumentViewModel {
  const comments = nullableString(dto.comments);
  const fileName = nullableString(dto.fileName);
  return {
    id: dto.id,
    documentType: narrowEnum<DocumentType>(
      dto.type ?? null,
      KNOWN_DOCUMENT_TYPE,
      'DOCUMENT',
    ),
    title: comments ?? fileName ?? '',
    fileName,
    createdAt: formatDateObserved(dto.dateObserved),
    createdBy: nullableString(dto.source?.label),
  };
}

export function mapDocuments(
  page: PageAttachmentDto | readonly AttachmentDto[] | null | undefined,
): DocumentViewModel[] {
  if (page === null || page === undefined) return [];
  const list: readonly AttachmentDto[] = Array.isArray(page)
    ? page
    : ((page as PageAttachmentDto).content ?? []);
  return list.map(mapDocument);
}
